// Identidad anónima estilo Nostr: al primer ingreso se genera un par de claves
// secp256k1; la privada queda SOLO en el dispositivo (localStorage) y la pública
// (pubkey) sirve de ID anónimo para la analítica. Mismo enfoque que el juego de GO
// del taller de IA (nostr-tools), pero sin publicar a relays.
import { generateSecretKey, getPublicKey, finalizeEvent } from 'nostr-tools/pure';
import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/spanish.js';

const KEY = 'm26:identity';

export interface Identity {
  sk: string; // clave privada (hex) — nunca sale del dispositivo
  pub: string; // clave pública (hex) — ID anónimo
  created: number;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

// ¿Es la primera vez? (consultar ANTES de getIdentity, que la crea si falta)
export function isFirstVisit(): boolean {
  try {
    return localStorage.getItem(KEY) === null;
  } catch {
    return false;
  }
}

export function getIdentity(): Identity {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Identity;
  } catch {
    /* localStorage no disponible */
  }
  const sk = generateSecretKey();
  const id: Identity = { sk: toHex(sk), pub: getPublicKey(sk), created: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(id));
  } catch {
    /* ignorar */
  }
  return id;
}

// Firma Schnorr (BIP340) de un objeto, con la clave del dispositivo. Sirve para que el
// backend pueda, a futuro, verificar la autoría de un pronóstico (hoy guarda la firma sin
// verificarla — ver endpoints/timba.php). Si algo falla, devuelve '' (no bloquea el envío).
export function signData(obj: unknown): string {
  try {
    const id = getIdentity();
    const ev = finalizeEvent(
      { kind: 27235, created_at: Math.floor(Date.now() / 1000), tags: [], content: JSON.stringify(obj) },
      fromHex(id.sk),
    );
    return ev.sig;
  } catch {
    return '';
  }
}

// Credencial de escritura (token) para la Timba: hash determinístico de la clave privada.
// Cualquier dispositivo con la misma clave la recalcula → no rompe "importar la clave".
// El backend hace TOFU: la fija la 1ª vez que ve el pub y después la exige, así nadie puede
// escribir con la identidad de otro (los pub son públicos en la tabla de posiciones).
// Devuelve '' si el contexto no es seguro (ej. HTTP en LAN) → el backend cae al modo sin protección.
export async function getWriteToken(): Promise<string> {
  try {
    const id = getIdentity();
    const data = new TextEncoder().encode('vamovamo:timba:write:' + id.sk);
    const buf = await crypto.subtle.digest('SHA-256', data);
    return toHex(new Uint8Array(buf));
  } catch {
    return '';
  }
}

// Respaldo: la clave secreta (hex) para guardar en lugar seguro y poder restaurar la
// identidad (mismos pronósticos y torneos) en otro dispositivo.
export function exportSecretKey(): string {
  return getIdentity().sk;
}

// Frase de recuperación: 24 palabras (en español) equivalentes a la clave privada. Es la MISMA
// identidad (la frase ES la entropía de la sk) → respaldo legible para restaurar en otro dispositivo.
export function exportSeedPhrase(): string {
  return entropyToMnemonic(fromHex(getIdentity().sk), wordlist);
}

// --- Restaurar desde un respaldo (frase de 24 palabras o clave hex) ---

const KNOWN_WORDS = new Set(wordlist);

function stripAccents(word: string): string {
  return word.normalize('NFD').replace(/[̀-ͯ]/g, ''); // quita las marcas de acento combinantes
}

// Mapa "palabra sin acentos" → palabra canónica del wordlist, SOLO para las que quedan inequívocas.
// El wordlist español lleva acentos (ábaco, dúo, órbita…) que el teclado o el autocorrect a veces
// comen al tipear/pegar. Con este mapa rescatamos esas frases sin arriesgar ambigüedad: si dos
// palabras colapsaran al quitar acentos, ninguna se mapea y la frase cae como inválida (con mensaje).
const ACCENTLESS_TO_WORD: Map<string, string> = (() => {
  const groups = new Map<string, string[]>();
  for (const word of wordlist) {
    const key = stripAccents(word);
    const list = groups.get(key) ?? [];
    list.push(word);
    groups.set(key, list);
  }
  const map = new Map<string, string>();
  for (const [key, list] of groups) {
    if (list.length === 1 && list[0] !== key) map.set(key, list[0]);
  }
  return map;
})();

// Repara acentos perdidos palabra por palabra (solo donde el desacentuado es inequívoco).
function repairPhraseAccents(phrase: string): string {
  return phrase
    .split(' ')
    .map((word) => (KNOWN_WORDS.has(word) ? word : ACCENTLESS_TO_WORD.get(word) ?? word))
    .join(' ');
}

// QUERY: computa la identidad desde un respaldo SIN persistirla, para poder confirmarla antes de
// adoptarla. Es la frontera de entrada: valida y falla con un mensaje accionable. A diferencia del
// flujo viejo, NUNCA interpreta una frase como clave hex (el bug: una frase que no validaba se
// machacaba a [a-f0-9] y, si el residuo daba 64 chars, devolvía una identidad equivocada "ok").
export function identityFromBackup(input: string): Identity {
  const raw = input.trim();
  if (raw === '') {
    throw new Error('Pegá tu frase de 24 palabras (o tu clave) para restaurar.');
  }
  if (/\s/.test(raw)) {
    // Tiene espacios → es una frase de palabras.
    const phrase = repairPhraseAccents(raw.toLowerCase().replace(/\s+/g, ' '));
    if (!validateMnemonic(phrase, wordlist)) {
      const count = phrase.split(' ').length;
      throw new Error(
        `La frase no es válida (${count} palabra${count === 1 ? '' : 's'}). Revisá que sean las 24 palabras correctas, en orden y bien escritas.`,
      );
    }
    const sk = toHex(mnemonicToEntropy(phrase, wordlist));
    return { sk, pub: getPublicKey(fromHex(sk)), created: Date.now() };
  }
  // Sin espacios → clave secreta en hexadecimal (respaldo viejo).
  const sk = raw.toLowerCase().replace(/[^a-f0-9]/g, '');
  if (sk.length !== 64) {
    throw new Error('La clave no es válida: deben ser 64 caracteres hexadecimales.');
  }
  return { sk, pub: getPublicKey(fromHex(sk)), created: Date.now() };
}

// COMMAND: adopta (persiste) una identidad ya computada y confirmada por el usuario.
export function adoptIdentity(id: Identity): void {
  localStorage.setItem(KEY, JSON.stringify(id));
}

// Compat: restaurar en un solo paso (computa y adopta). Para callers que no necesitan confirmación.
export function restoreIdentity(input: string): Identity {
  const id = identityFromBackup(input);
  adoptIdentity(id);
  return id;
}
